import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { cardPath } from '@/lib/cardSlug';
import { LoadingSpinner } from '@/components/LoadingSpinner';

const SITE = 'https://lorescout.com';

interface PriceRow {
  id: string;
  treatment: string;
  rarity: string | null;
  price_usd: number | null;
  price_usd_updated: string | null;
  cards: {
    id: string;
    name: string;
    subtitle: string | null;
    set_name: string;
    ink_color: string;
    rarity: string;
  } | null;
}

type SortKey = 'top' | 'name';
type TreatmentFilter = 'all' | 'normal' | 'holofoil' | 'enchanted' | 'iconic';

function fmtPrice(n: number | null) {
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
}

function fmtRelative(iso: string | null) {
  if (!iso) return 'unknown';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function PricesPage() {
  const [sort, setSort] = useState<SortKey>('top');
  const [treatment, setTreatment] = useState<TreatmentFilter>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['public-prices', sort, treatment],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      let q = supabase
        .from('card_prints')
        .select('id,treatment,rarity,price_usd,price_usd_updated,cards(id,name,subtitle,set_name,ink_color,rarity)')
        .not('price_usd', 'is', null);

      if (treatment !== 'all') q = q.eq('treatment', treatment);

      q = sort === 'top'
        ? q.order('price_usd', { ascending: false }).limit(200)
        : q.order('price_usd', { ascending: false }).limit(500);

      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as unknown as PriceRow[];
    },
  });

  const rows = useMemo(() => {
    const list = (data || []).filter((r) => r.cards);
    if (sort === 'name') {
      return [...list].sort((a, b) => (a.cards!.name).localeCompare(b.cards!.name));
    }
    return list;
  }, [data, sort]);

  const description = `Live Disney Lorcana card prices, updated daily. Browse the top-${rows.length || 200} most valuable Lorcana cards across all sets and treatments with historical price charts on LoreScout.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Disney Lorcana Card Prices',
    numberOfItems: rows.length,
    itemListElement: rows.slice(0, 50).map((r, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE}${cardPath(r.cards!)}`,
      name: r.cards!.subtitle ? `${r.cards!.name} — ${r.cards!.subtitle}` : r.cards!.name,
    })),
  };

  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>Lorcana Card Prices — Live Market Data · LoreScout</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE}/prices`} />
        <meta property="og:title" content="Disney Lorcana Card Prices — Live Market Data" />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${SITE}/prices`} />
        <meta name="twitter:title" content="Disney Lorcana Card Prices — Live Market Data" />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}<span className="text-foreground">Prices</span>
        </nav>
        <h1 className="font-serif text-3xl">Lorcana Card Prices</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Live market prices for Disney Lorcana cards, refreshed daily from JustTCG. Click any card for
          full price history charts across every treatment.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-md border border-border p-1">
            {(['top', 'name'] as SortKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setSort(k)}
                className={`px-3 py-1 text-xs rounded ${sort === k ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
              >
                {k === 'top' ? 'Most Valuable' : 'A–Z'}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border border-border p-1">
            {(['all', 'normal', 'holofoil', 'enchanted', 'iconic'] as TreatmentFilter[]).map((t) => (
              <button
                key={t}
                onClick={() => setTreatment(t)}
                className={`px-3 py-1 text-xs rounded capitalize ${treatment === t ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {isLoading && <div className="py-12 flex justify-center"><LoadingSpinner /></div>}

        {!isLoading && rows.length === 0 && (
          <p className="mt-8 text-sm text-muted-foreground">No priced cards found for this filter.</p>
        )}

        {!isLoading && rows.length > 0 && (
          <div className="mt-4 overflow-x-auto border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground bg-muted/30">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Card</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Set</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Treatment</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-right px-3 py-2 hidden md:table-cell">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const c = r.cards!;
                  return (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/20">
                      <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-2">
                        <Link to={cardPath(c)} className="hover:underline">
                          <span className="font-medium">{c.name}</span>
                          {c.subtitle && <span className="text-muted-foreground"> — {c.subtitle}</span>}
                        </Link>
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell text-muted-foreground">{c.set_name}</td>
                      <td className="px-3 py-2 hidden md:table-cell capitalize text-muted-foreground">{r.treatment}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtPrice(r.price_usd)}</td>
                      <td className="px-3 py-2 hidden md:table-cell text-right text-xs text-muted-foreground">{fmtRelative(r.price_usd_updated)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-xs text-muted-foreground">
          Prices in USD sourced from TCGplayer via JustTCG. Market prices fluctuate — figures shown
          reflect the most recent snapshot.
        </p>
      </article>
    </div>
  );
}
